import { isActivePublishJob } from '@course-authoring/ui-blocks';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublishJobDto, QuestionListItem } from '@course-authoring/shared-types';
import { fetchQuestionDetail, fetchQuestionPublishJob } from '../../api/questionApi';
import { PUBLISH_JOB_POLL_INTERVAL_MS } from '../../utils/publishJobPolling';

interface PreparationState {
  questionId: number;
  locale: string;
  job: PublishJobDto | null;
  warning: string | null;
  ready: boolean;
  refreshing: boolean;
}

function jobSignature(job: PublishJobDto | null): string {
  return job ? `${job.id}:${job.attempt}:${job.status}` : 'none';
}

/** The editor polls its exact resource even while idle, so work started in another tab is visible. */
export function useQuestionPreparation(
  questionId: number | undefined,
  locale: string,
  isOpen: boolean,
  onDetail: (question: QuestionListItem) => void,
) {
  const [state, setState] = useState<PreparationState | null>(null);
  const stateRef = useRef<PreparationState | null>(null);
  const readySignatureRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const scopeRef = useRef({ key: '', generation: 0 });
  const scopeKey = `${questionId ?? 'none'}:${locale}:${isOpen}`;
  if (scopeRef.current.key !== scopeKey) {
    scopeRef.current = { key: scopeKey, generation: scopeRef.current.generation + 1 };
    inFlightRef.current = false;
    readySignatureRef.current = null;
  }
  const onDetailRef = useRef(onDetail);
  onDetailRef.current = onDetail;

  const poll = useCallback(async () => {
    if (!isOpen || questionId === undefined || inFlightRef.current || document.visibilityState === 'hidden') return;
    const generation = scopeRef.current.generation;
    inFlightRef.current = true;
    try {
      const job = await fetchQuestionPublishJob(questionId);
      if (scopeRef.current.generation !== generation) return;
      const previous = stateRef.current?.questionId === questionId && stateRef.current.locale === locale ? stateRef.current : null;
      const signature = jobSignature(job);
      const mustRefresh = readySignatureRef.current !== signature
        && (!isActivePublishJob(job) || readySignatureRef.current === null);
      if (mustRefresh) {
        const waiting: PreparationState = {
          questionId,
          locale,
          job: previous?.job ?? job,
          warning: null,
          ready: previous?.ready ?? false,
          refreshing: true,
        };
        stateRef.current = waiting;
        setState(waiting);
        const detail = await fetchQuestionDetail(questionId, locale);
        if (scopeRef.current.generation !== generation) return;
        onDetailRef.current(detail);
      }
      readySignatureRef.current = signature;
      const next: PreparationState = { questionId, locale, job, warning: null, ready: true, refreshing: false };
      stateRef.current = next;
      setState(next);
    } catch {
      if (scopeRef.current.generation !== generation) return;
      const previous = stateRef.current?.questionId === questionId && stateRef.current.locale === locale ? stateRef.current : null;
      const next: PreparationState = {
        questionId,
        locale,
        job: previous?.job ?? null,
        warning: 'Preparation status or question content could not be refreshed. Try again shortly.',
        ready: previous?.ready ?? false,
        refreshing: previous?.refreshing ?? false,
      };
      stateRef.current = next;
      setState(next);
    } finally {
      if (scopeRef.current.generation === generation) inFlightRef.current = false;
    }
  }, [isOpen, questionId, locale]);

  useEffect(() => {
    if (!isOpen || questionId === undefined) return;
    const effectScopeKey = scopeRef.current.key;
    readySignatureRef.current = null;
    stateRef.current = null;
    setState(null);
    void poll();
    const interval = window.setInterval(() => { void poll(); }, PUBLISH_JOB_POLL_INTERVAL_MS);
    const refetchWhenVisible = () => {
      if (document.visibilityState !== 'hidden') void poll();
    };
    document.addEventListener('visibilitychange', refetchWhenVisible);
    window.addEventListener('focus', refetchWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refetchWhenVisible);
      window.removeEventListener('focus', refetchWhenVisible);
      if (scopeRef.current.key === effectScopeKey) {
        scopeRef.current = { ...scopeRef.current, generation: scopeRef.current.generation + 1 };
        inFlightRef.current = false;
      }
    };
  }, [isOpen, questionId, poll]);

  const current = state && state.questionId === questionId && state.locale === locale ? state : null;
  const invalidatePendingRead = () => {
    scopeRef.current = { ...scopeRef.current, generation: scopeRef.current.generation + 1 };
    inFlightRef.current = false;
  };
  const acceptJob = (job: PublishJobDto | null) => {
    if (questionId === undefined) return;
    invalidatePendingRead();
    const next: PreparationState = { questionId, locale, job, warning: null, ready: true, refreshing: false };
    stateRef.current = next;
    setState(next);
    readySignatureRef.current = jobSignature(job);
  };

  const requireStatus = () => {
    if (questionId === undefined) return;
    invalidatePendingRead();
    const next: PreparationState = {
      questionId,
      locale,
      job: current?.job ?? null,
      warning: null,
      ready: false,
      refreshing: false,
    };
    stateRef.current = next;
    setState(next);
    readySignatureRef.current = null;
  };

  return {
    job: current?.job ?? null,
    warning: current?.warning ?? null,
    ready: current?.ready ?? false,
    refreshing: current?.refreshing ?? false,
    poll,
    acceptJob,
    requireStatus,
  };
}
